import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Descriptions
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import '../../styles/third-party.css';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

const auditStatusMap = {
  pending: { tone: 'processing', text: '待审核' },
  approved: { tone: 'paid', text: '已通过' },
  rejected: { tone: 'rejected', text: '已拒绝' }
};

const formatMoney = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('en-US');
};

const SmsRecordList = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/sms-records', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          ...filters,
          ...params
        }
      });

      if (response.code === 200) {
        setDataSource(response.data.list);
        setPagination({
          ...pagination,
          total: response.data.pagination.total,
          current: response.data.pagination.page
        });
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/admin/sms-records/statistics', {
        params: filters
      });

      if (response.code === 200) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('加载统计数据失败', error);
    }
  };

  useEffect(() => {
    loadData();
    loadStatistics();
  }, []);

  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;

    setFilters(newFilters);
    setPagination({ ...pagination, current: 1 });
    loadData({ ...newFilters, page: 1 });
    loadStatistics();
  };

  const handleReset = () => {
    searchForm.resetFields();
    setFilters({});
    setPagination({ ...pagination, current: 1 });
    loadData({ page: 1 });
    loadStatistics();
  };

  const handleViewDetail = (record) => {
    setCurrentRecord(record);
    setDetailModalVisible(true);
  };

  const handleAudit = (record) => {
    setCurrentRecord(record);
    form.setFieldsValue({
      auditStatus: 'approved',
      auditRemark: ''
    });
    setAuditModalVisible(true);
  };

  const handleAuditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const response = await api.put(
        `/admin/sms-records/${currentRecord.id}/audit`,
        values
      );

      if (response.code === 200) {
        message.success('审核成功');
        setAuditModalVisible(false);
        loadData();
        loadStatistics();
      }
    } catch (error) {
      message.error('审核失败');
    }
  };

  const handleBatchAudit = async (auditStatus) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要审核的记录');
      return;
    }

    Modal.confirm({
      title: `确认批量${auditStatus === 'approved' ? '通过' : '拒绝'}？`,
      content: `将对选中的 ${selectedRowKeys.length} 条记录进行审核`,
      onOk: async () => {
        try {
          const response = await api.put('/admin/sms-records/batch/audit', {
            ids: selectedRowKeys,
            auditStatus,
            auditRemark: `批量${auditStatus === 'approved' ? '通过' : '拒绝'}`
          });

          if (response.code === 200) {
            message.success(response.message);
            setSelectedRowKeys([]);
            loadData();
            loadStatistics();
          }
        } catch (error) {
          message.error('批量审核失败');
        }
      }
    });
  };

  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`/admin/sms-records/${id}`);
      if (response.code === 200) {
        message.success('删除成功');
        loadData();
        loadStatistics();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的记录');
      return;
    }

    Modal.confirm({
      title: '确认删除？',
      content: `将删除选中的 ${selectedRowKeys.length} 条记录，此操作不可恢复`,
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await api.delete('/admin/sms-records/batch/delete', {
            data: { ids: selectedRowKeys }
          });

          if (response.code === 200) {
            message.success(response.message);
            setSelectedRowKeys([]);
            loadData();
            loadStatistics();
          }
        } catch (error) {
          message.error('批量删除失败');
        }
      }
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      render: (val) => val || <span className="tp-empty">-</span>
    },
    {
      title: '设备ID',
      dataIndex: 'device_id',
      width: 120,
      render: (id) => id ? <span className="tp-ellipsis" title={id}>{id}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '交易代码',
      dataIndex: 'transaction_code',
      width: 130,
      render: (code) => code ? <span className="tp-ellipsis" title={code}>{code}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '发送方',
      dataIndex: 'sender',
      width: 120,
      render: (sender) => sender ? <span className="tp-ellipsis" title={sender}>{sender}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '手机号',
      dataIndex: 'account',
      width: 120,
      render: (account) => account ? <span className="tp-ellipsis" title={account}>{account}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 100,
      render: (amount) => {
        const text = formatMoney(amount);
        return text
          ? (
            <div className="tp-amount">
              <span className="tp-amount__value">{text}</span>
              <span className="tp-amount__ccy">Ksh</span>
            </div>
          )
          : <span className="tp-empty">-</span>;
      }
    },
    {
      title: '余额',
      dataIndex: 'balance',
      width: 100,
      render: (balance) => {
        const text = formatMoney(balance);
        return text
          ? (
            <div className="tp-amount">
              <span className="tp-amount__value">{text}</span>
              <span className="tp-amount__ccy">Ksh</span>
            </div>
          )
          : <span className="tp-empty">-</span>;
      }
    },
    {
      title: '交易时间',
      dataIndex: 'datetime',
      width: 150,
      render: (datetime) => datetime ? <span className="tp-time">{datetime}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '审核状态',
      dataIndex: 'audit_status',
      width: 100,
      render: (status) => {
        const config = auditStatusMap[status] || { tone: 'muted', text: status };
        return <span className={`tp-tag tp-tag--${config.tone}`}>{config.text}</span>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (time) => time ? <span className="tp-time">{dayjs(time).format('YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <div className="tp-actions">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {record.audit_status === 'pending' && (
            <Button
              type="link"
              size="small"
              className="tp-link--ok"
              onClick={() => handleAudit(record)}
            >
              审核
            </Button>
          )}
          <Popconfirm
            title="确认删除？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button
              type="link"
              size="small"
              className="tp-link--danger"
            >
              删除
            </Button>
          </Popconfirm>
        </div>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys)
  };

  return (
    <div className="tp-page">
      {statistics && (
        <div className="tp-card tp-summary">
          <div className="tp-summary__item">
            <div className="tp-summary__label">总记录数</div>
            <div className="tp-summary__value tp-summary__value--accent">
              {Number(statistics.totalCount || 0).toLocaleString('en-US')}
            </div>
          </div>
          {(statistics.byStatus || []).map((stat) => {
            const tone = stat.status === 'approved' ? 'green' : '';
            return (
              <div className="tp-summary__item" key={stat.status}>
                <div className="tp-summary__label">{stat.statusName}</div>
                <div className={`tp-summary__value ${tone ? 'tp-summary__value--green' : ''}`}>
                  {Number(stat.count || 0).toLocaleString('en-US')}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="tp-card tp-toolbar">
        <Form form={searchForm} layout="inline" className="tp-filters" onFinish={handleSearch}>
          <Form.Item name="deviceId">
            <Input placeholder="设备ID" className="tp-field--md" allowClear />
          </Form.Item>
          <Form.Item name="account">
            <Input placeholder="账号" className="tp-field--md" allowClear />
          </Form.Item>
          <Form.Item name="auditStatus">
            <Select placeholder="审核状态" className="tp-field--sm" allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker className="tp-field--date" placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <Form.Item name="keyword">
            <Input placeholder="搜索内容或账号" className="tp-field--lg" allowClear />
          </Form.Item>
          <div className="tp-filters__actions">
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
            <Button onClick={handleReset} icon={<ReloadOutlined />}>重置</Button>
          </div>
        </Form>
      </div>

      <div className="tp-card tp-table-card">
        <div className="tp-table-head">
          <div className="tp-filters__actions">
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handleBatchAudit('approved')}
              disabled={selectedRowKeys.length === 0}
            >
              批量通过
            </Button>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleBatchAudit('rejected')}
              disabled={selectedRowKeys.length === 0}
            >
              批量拒绝
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={selectedRowKeys.length === 0}
            >
              批量删除
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => loadData()}>
              刷新
            </Button>
          </div>
          <div className="tp-table-head__count">共 {pagination.total} 条记录</div>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          size="middle"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          rowSelection={rowSelection}
          onChange={(newPagination) => {
            setPagination(newPagination);
            loadData({ page: newPagination.current, limit: newPagination.pageSize });
          }}
          scroll={{ x: 1400 }}
        />
      </div>

      <Modal
        className="tp-modal"
        title="审核短信记录"
        open={auditModalVisible}
        onOk={handleAuditSubmit}
        onCancel={() => setAuditModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="auditStatus"
            label="审核结果"
            rules={[{ required: true, message: '请选择审核结果' }]}
          >
            <Select>
              <Option value="approved">通过</Option>
              <Option value="rejected">拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="auditRemark" label="审核备注">
            <TextArea rows={4} placeholder="请输入审核备注" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        className="tp-modal"
        title="短信记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {currentRecord && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="ID">{currentRecord.id}</Descriptions.Item>
            <Descriptions.Item label="设备ID">{currentRecord.device_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="交易代码">{currentRecord.transaction_code || '-'}</Descriptions.Item>
            <Descriptions.Item label="发送方">{currentRecord.sender || '-'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{currentRecord.account || '-'}</Descriptions.Item>
            <Descriptions.Item label="金额">
              {formatMoney(currentRecord.amount) ? `Ksh ${formatMoney(currentRecord.amount)}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="余额">
              {formatMoney(currentRecord.balance) ? `Ksh ${formatMoney(currentRecord.balance)}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="交易时间">
              {currentRecord.datetime
                ? <span className="tp-time">{currentRecord.datetime}</span>
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="短信内容" span={2}>
              <div className="tp-pre">{currentRecord.content || '-'}</div>
            </Descriptions.Item>
            <Descriptions.Item label="审核状态" span={2}>
              <span className={`tp-tag tp-tag--${auditStatusMap[currentRecord.audit_status]?.tone || 'muted'}`}>
                {auditStatusMap[currentRecord.audit_status]?.text || currentRecord.audit_status}
              </span>
            </Descriptions.Item>
            {currentRecord.audit_remark && (
              <Descriptions.Item label="审核备注" span={2}>
                <div className="tp-pre">{currentRecord.audit_remark}</div>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建时间">
              <span className="tp-time">{dayjs(currentRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}</span>
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              <span className="tp-time">{dayjs(currentRecord.updated_at).format('YYYY-MM-DD HH:mm:ss')}</span>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default SmsRecordList;
